import type { EditorStarterItem } from '../items/item-type';
import type { TrackType } from '../state/types';

interface GetConstrainedTimelineMoveDeltaParams {
  itemIds: string[];
  requestedDelta: number;
  items: Record<string, EditorStarterItem>;
  tracks: TrackType[];
}

export const getConstrainedTimelineMoveDelta = ({
  itemIds,
  requestedDelta,
  items,
  tracks,
}: GetConstrainedTimelineMoveDeltaParams): number => {
  if (requestedDelta === 0 || itemIds.length === 0) {
    return 0;
  }

  const movingItemIds = new Set(itemIds);
  let hasMovingTrackItem = false;
  let minDelta = Number.NEGATIVE_INFINITY;
  let maxDelta = Number.POSITIVE_INFINITY;

  for (const track of tracks) {
    const movingItemsOnTrack = track.items
      .filter((itemId) => movingItemIds.has(itemId))
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => item !== undefined);

    if (movingItemsOnTrack.length === 0) {
      continue;
    }

    hasMovingTrackItem = true;

    const stationaryItemsOnTrack = track.items
      .filter((itemId) => !movingItemIds.has(itemId))
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => item !== undefined);

    for (const movingItem of movingItemsOnTrack) {
      const movingEnd = movingItem.from + movingItem.durationInFrames;
      minDelta = Math.max(minDelta, -movingItem.from);

      for (const stationaryItem of stationaryItemsOnTrack) {
        const stationaryEnd =
          stationaryItem.from + stationaryItem.durationInFrames;

        if (stationaryEnd <= movingItem.from) {
          minDelta = Math.max(minDelta, stationaryEnd - movingItem.from);
        } else if (stationaryItem.from >= movingEnd) {
          maxDelta = Math.min(maxDelta, stationaryItem.from - movingEnd);
        }
      }
    }
  }

  if (!hasMovingTrackItem || minDelta > maxDelta) {
    return 0;
  }

  if (requestedDelta > 0) {
    return Math.max(0, Math.min(requestedDelta, maxDelta));
  }

  return Math.min(0, Math.max(requestedDelta, minDelta));
};
